import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Phone } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.5 } }),
};

const FAQS = [
  {
    category: "General",
    questions: [
      { q: "How does the comparison process work?", a: "Simply tell us what you need, share some basic details about your current setup such as your postcode and current provider, and our team will find you a better electricity plan. We present the best options to you and, if you choose to switch, we handle the entire process." },
      { q: "Is the comparison service free?", a: "Yes, our comparison service is 100% free for both residential and business customers. We are paid a referral fee by providers when you switch through us, so there is never a cost to you. This model means we're incentivised to find you the best deal possible." },
      { q: "How long does the comparison take?", a: "The initial comparison form takes under 2 minutes to complete. Once submitted, our team will review your details and contact you with personalised comparison results, typically within 24 hours. The actual switching process usually takes 1-3 business days depending on the service type." },
      { q: "Is there any obligation to switch?", a: "Absolutely not. Our comparison is completely no-obligation. We present you with the best options we can find, and it's entirely your decision whether to switch or stay with your current provider. There's zero pressure." },
    ],
  },
  {
    category: "Coverage & Providers",
    questions: [
      { q: "Which providers do you compare?", a: "We work with over 50 providers across energy, gas, internet, solar, insurance, and other essential services. Our provider network includes major names like AGL, Origin Energy, Energy Australia, Alinta Energy, Telstra, Optus, and many more. We don't compare every provider in the market, but we do cover the majority of options available." },
      { q: "Can you help customers anywhere in Australia?", a: "Yes! While our team is based in Sydney, we service all of Australia - from major cities to regional and rural areas. Our comparison tools cover providers and plans available across NSW, VIC, QLD, SA, WA, TAS, ACT, and NT." },
      { q: "Do you compare all brands and products?", a: "We compare a wide range of brands and products, but not every single option in the market. Some products and offers may only be available through our call centre or website. We're always expanding our provider network to give you the broadest possible comparison." },
    ],
  },
  {
    category: "Business Customers",
    questions: [
      { q: "Can you help businesses?", a: "Absolutely. We work with businesses of all sizes, from sole traders and small businesses to large commercial operations. We offer specialised business comparisons for energy, gas, internet, phone systems, insurance, merchant services, and more." },
      { q: "Do you handle multi-site businesses?", a: "Yes, we can manage comparisons and switching for businesses with multiple locations. Our business energy team specialises in multi-site energy management and can often negotiate better rates due to combined usage volumes." },
      { q: "Can you help with business energy contracts?", a: "Yes. Our business energy specialists can review your existing contracts, negotiate renewals, and find you a better plan. We understand the complexities of business energy tariffs including demand charges, time-of-use rates, and peak/off-peak pricing." },
    ],
  },
  {
    category: "Switching Process",
    questions: [
      { q: "What information do I need to provide?", a: "For a basic comparison, you'll need your postcode, an idea of your current provider, and your approximate bill amount. For a more detailed comparison, having a recent bill handy is helpful but not required. For business comparisons, having your ABN and recent utility bills will allow us to provide the most accurate comparison." },
      { q: "Will switching interrupt my service?", a: "No. Switching between providers does not interrupt your service. For energy and gas, there's no physical change to your connection - it's simply an administrative switch between providers. For internet, there may be brief downtime during the switchover, but your new provider will coordinate this to minimise any disruption." },
      { q: "How long does the switch take?", a: "For energy and gas, switching typically takes 1-3 business days. For internet, it can take 5-10 business days depending on the type of connection. For other services like insurance, the switch can often be immediate or aligned with your current policy renewal date." },
      { q: "Can I switch back to my old provider?", a: "Yes, you're always free to switch providers at any time, including going back to your previous provider. However, the plan you were on may no longer be available. We recommend using our comparison service whenever your circumstances change to ensure you're always on the best deal." },
    ],
  },
  {
    category: "Solar & Green Energy",
    questions: [
      { q: "Can you help with solar panel comparison?", a: "Yes, we compare solar panel installation options, feed-in tariffs, and battery storage solutions. Whether you're looking to install solar for the first time or optimise your existing system, our team can help you find the best deal." },
      { q: "Do you compare green energy plans?", a: "Yes, we can help you find energy plans that include renewable energy options. Many providers offer plans with a percentage of green energy or carbon offsets. We'll help you find plans that align with your sustainability goals while still saving money." },
    ],
  },
];

const HERO_BG = "https://images.unsplash.com/photo-1559027877-de64d8a44d57?w=1920&q=80";

export default function FAQPage() {
  return (
    <div data-testid="faq-page">
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0B203B]/90" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="max-w-2xl" initial="hidden" animate="visible" variants={fadeUp}>
            <span className="text-sm font-semibold tracking-wider uppercase text-[#FF6600]">FAQ</span>
            <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-white mt-2 mb-4">
              Frequently Asked Questions
            </h1>
            <p className="text-lg text-gray-300 leading-relaxed">
              Everything you need to know about our comparison service. Can't find your answer? Get in touch - we're happy to help.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          {FAQS.map((cat, ci) => (
            <motion.div key={ci} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <h2 className="font-heading text-2xl font-bold text-[#2E2E33] mb-4">{cat.category}</h2>
              <Accordion type="single" collapsible className="space-y-3">
                {cat.questions.map((faq, fi) => (
                  <AccordionItem
                    key={fi}
                    value={`${ci}-${fi}`}
                    className="bg-white border border-[#E2E8F0] rounded-xl px-6 overflow-hidden"
                  >
                    <AccordionTrigger
                      className="text-left font-heading font-semibold text-[#0B203B] hover:text-[#FF6600] py-4 text-sm md:text-base"
                      data-testid={`faq-trigger-${ci}-${fi}`}
                    >
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-[#64748B] text-sm leading-relaxed pb-4">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Still have questions */}
      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading text-3xl font-bold text-[#2E2E33] mb-4">Still Have Questions?</h2>
          <p className="text-[#64748B] mb-8">Our friendly team is always happy to help. Get in touch and we'll get back to you as soon as possible.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-[#FF6600] hover:bg-[#E55C00] text-white font-bold rounded-full px-8 py-3" data-testid="faq-contact-btn">
                Contact Us <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <a href="tel:1300796797">
              <Button variant="outline" className="border-2 border-[#2E2E33] text-[#2E2E33] font-bold rounded-lg px-8 py-3 hover:bg-[#2E2E33] hover:text-white" data-testid="faq-call-btn">
                <Phone className="w-4 h-4 mr-2" /> 1300 796 797
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* CTA with background */}
      <section className="relative py-16 overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" style={{ transform: "scaleX(-1)" }} />
          <div className="absolute inset-0 bg-[#0B203B]/90" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading text-3xl font-bold text-white mb-4">Ready to Start Saving?</h2>
          <p className="text-gray-300 mb-8">Join thousands of Australians who have already found better deals with Sales Corp Australia.</p>
          <Link to="/compare">
            <Button className="bg-[#FF6600] hover:bg-[#E55C00] text-white font-bold rounded-lg px-8 py-4 text-lg" data-testid="faq-cta-compare">
              Compare Now <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
